import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography, Select, MenuItem } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import Link from '@mui/material/Link';

import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from "@mui/icons-material/Security";
import VerifiedUser from "@mui/icons-material/VerifiedUser";

import SecurityDialog from "components/dialogs/SecurityDialog";
import APIRequest from "system/APIRequest";
import { currentUser, InfegyUser } from "system/InfegyUser";
import { ApiTokenPreview } from "./ManageApiTokens";
import { useSnackbar } from "notistack";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";

enum CreateApiTokenState {
  Idle,
  ConfirmPassword,
  CreatingToken,
  Success
}

interface CreateApiTokenProps {
  /**
   * If `true`, the component is shown.
   * @default false
   */
  isOpen: boolean;
  /**
   * Callback fired when the component requests to be closed.
   */
  onClose: () => void;
  /**
   * Callback fired when the component successfully created an API Token.
   */
  onSuccess: (apiToken: ApiTokenPreview) => void;
  /**
   * The InfegyUser the new API Token belongs to.
   */
  user: InfegyUser;
}

export default function CreateApiToken(props: CreateApiTokenProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const thisUser = currentUser();

  const [state, setState] = useState(CreateApiTokenState.Idle);
  const [description, setDescription] = useState("");
  const [expiration, setExpiration] = useState("P1Y"); // Default to 1 year (ISO8601 period)
  const [securityErrorMessage, setSecurityErrorMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  function handleShowSecurityDialog(): void {
    setState(CreateApiTokenState.ConfirmPassword);
  }

  function handleCloseSecurityDialog(): void {
    setSecurityErrorMessage("");
    setState(CreateApiTokenState.Idle);
  }

  async function handleCreateApiToken(currentPassword: string): Promise<void> {
    const body = {
      password: currentPassword,
      description,
      email: props.user.email
    };
    let response: ApiTokenPreview;
    try {
      setState(CreateApiTokenState.CreatingToken);
      response = await APIRequest("create-token", {expires_at: expiration}, {
        method: "POST",
        body: JSON.stringify(body)
      });
      setState(CreateApiTokenState.Success);
      // Buffer the success to show UI feedback
      setTimeout(() => {
        enqueueSnackbar(i18n.t("manage-api-tokens.create-token-success"), { variant: "success" });
        props.onSuccess(response);
      }, 1000);
    } catch (e: any) {
      if (e.code === 401 || e.code === 403) {
        // This is a failure to authenticate.  The user provided an invalid current password.
        setState(CreateApiTokenState.ConfirmPassword);
        setSecurityErrorMessage(e.server_message);
      } else {
        // Some other error happened and we should still display feedback to the user.
        setState(CreateApiTokenState.Idle);
        setErrorMessage(e.server_message);
      }
    }
  }

  function handleDescriptionChange(event: React.ChangeEvent<HTMLInputElement>) {
    setDescription(event.target.value);
  }

  function handleExpirationChange(event: SelectChangeEvent<string>): void {
    setExpiration(event.target.value);
  }

  function getCreateTokenButtonIcon(state: CreateApiTokenState): React.JSX.Element | null {
    switch (state) {
      case CreateApiTokenState.Idle:
      case CreateApiTokenState.ConfirmPassword:
      // It seems that MUI wants a button to be set, even if the button is not used, when the
      // loading spinner is being displayed.  Without the button set, the loading spinner will
      // display on top of the text of the button instead of next to it.
      case CreateApiTokenState.CreatingToken:
        return <SecurityIcon />;
      case CreateApiTokenState.Success:
        return <VerifiedUser />;
    }
  }

  const billingOrderId = thisUser?.account.billing_order_id ?? thisUser?.account.trial_order_id;
  let billingOrderUrl = `/billing/order/view/${billingOrderId}`;
  if (window.location.hostname === "localhost") {
    billingOrderUrl = `https://starscape-staging.infegy.com/billing/order/view/${billingOrderId}`;
  }

  return (
    <Dialog open={props.isOpen} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h3">{i18n.t("create-api-token.title")}</Typography>
      </DialogTitle>
      <IconButton onClick={props.onClose} sx={{position: "absolute", right: 8, top: 8}}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>        
        {/* Description  */}
        <Stack spacing={1}>
          <SimpleFormLabel>{i18n.t("create-api-token.description")}</SimpleFormLabel>
          <Box>
            <TextField 
              fullWidth 
              autoComplete="off" 
              onChange={handleDescriptionChange} 
              placeholder={i18n.t("create-api-token.info")} />
          </Box>
        </Stack>

        {/* Expiration */}
        <Stack spacing={1} sx={{
          marginTop: 1
        }}>
          <SimpleFormLabel>{i18n.t("create-api-token.expiration")}</SimpleFormLabel>
          <Box>
            <Select
              fullWidth
              value={expiration}
              onChange={handleExpirationChange}
            >
              <MenuItem value={"P1D"}>1 {i18n.t("day")}</MenuItem>
              <MenuItem value={"P7D"}>7 {i18n.t("days")}</MenuItem>
              <MenuItem value={"P30D"}>30 {i18n.t("days")}</MenuItem>
              <MenuItem value={"P6M"}>6 {i18n.t("months")}</MenuItem>
              <MenuItem value={"P1Y"}>12 {i18n.t("months")} - {i18n.t("default")}</MenuItem>
              <MenuItem value={"P2Y"}>2 {i18n.t("years")}</MenuItem>
              <MenuItem value={"P3Y"}>3 {i18n.t("years")}</MenuItem>
              <MenuItem value={"P5Y"}>5 {i18n.t("years")}</MenuItem>
              <MenuItem value={"P10Y"}>10 {i18n.t("years")}</MenuItem>
            </Select>
          </Box>
        </Stack>

        {/* Information & Alerts */}
        <Stack spacing={1} sx={{
          marginTop: 1
        }}>
          {errorMessage && <Alert variant="filled" severity="error">{errorMessage}</Alert>}
          <Alert severity="warning" variant="outlined">
            {i18n.t("create-api-token.cost-warning")}
            {billingOrderId &&
              <Box>
                <Link href={billingOrderUrl} target="_blank" rel="noreferrer" >
                  {i18n.t("create-api-token.cost-link-text")}
                </Link>
              </Box>
            }
          </Alert>
        </Stack>

        {/* Action Buttons */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: "end",
            marginTop: 2
          }}>
          <Button variant="contained" color="secondary" onClick={props.onClose}>{i18n.t("create-api-token.cancel-button")}</Button>
          <Button
            disabled={description.length === 0}
            loading={state === CreateApiTokenState.CreatingToken}
            loadingPosition="start"
            onClick={handleShowSecurityDialog}
            startIcon={getCreateTokenButtonIcon(state)}
            variant="contained" >
            {i18n.t("create-api-token.create-button")}
          </Button>
        </Stack>
      </DialogContent>
      {state === CreateApiTokenState.ConfirmPassword &&
        <SecurityDialog isOpen={true} errorMessage={securityErrorMessage} onSubmit={handleCreateApiToken} onClose={handleCloseSecurityDialog} />
      }
    </Dialog>
  );
}